import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Wrapper } from '@googlemaps/react-wrapper';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

// NOTE: If you already add the script tag in public/index.html with your key, you can
//       keep this key here unused or remove Wrapper and mount the map directly.
//       Wrapper is convenient for loading the API in React.
const GOOGLE_MAPS_API_KEY = "AIzaSyBB1thRBxM3w6ugBM1AaRQgbqNVipMByoc";

function ResultsMap({ results, onPlaceSelect, searchType }) {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const markersRef = useRef([]);
  const clustererRef = useRef(null);
  const infoWindowRef = useRef(null);

  const initializeMap = useCallback((node) => {
    if (!node || map) return;
    if (!window.google || !window.google.maps) return;

    // Compute bounds to include midpoint and all places
    const bounds = new window.google.maps.LatLngBounds();
    if (results?.midpoint) {
      bounds.extend({ lat: results.midpoint.lat, lng: results.midpoint.lng });
    }
    (results?.restaurants || []).forEach((place) => {
      const loc = place.geometry?.location;
      const lat = loc ? (typeof loc.lat === 'function' ? loc.lat() : loc.lat) : null;
      const lng = loc ? (typeof loc.lng === 'function' ? loc.lng() : loc.lng) : null;
      if (lat != null && lng != null) bounds.extend({ lat, lng });
    });

    const newMap = new window.google.maps.Map(node, {
      zoom: 13,
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
    });

    if (!bounds.isEmpty()) {
      const padding = { top: 50, bottom: 50, left: 50, right: 50 };
      newMap.fitBounds(bounds, padding);
    }

    infoWindowRef.current = new window.google.maps.InfoWindow();
    mapRef.current = newMap;
    setMap(newMap);
  }, [map, results]);

  useEffect(() => {
    if (!map || !results) return;

    // Clear previous markers & clusterer
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current = null;
    }
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    // Midpoint marker
    if (results.midpoint) {
      const midpointMarker = new window.google.maps.Marker({
        position: { lat: results.midpoint.lat, lng: results.midpoint.lng },
        map,
        title: 'Point de rencontre',
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
              <circle cx="16" cy="16" r="14" fill="#3b82f6" stroke="white" stroke-width="3"/>
              <circle cx="16" cy="16" r="6" fill="white"/>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(32, 32),
        },
      });

      midpointMarker.addListener('click', () => {
        if (!infoWindowRef.current) return;
        infoWindowRef.current.setContent(`
          <div class="p-2">
            <h3 class="font-bold text-blue-600">Point de rencontre</h3>
            <p class="text-sm text-gray-600">${results.midpoint.address || ''}</p>
          </div>
        `);
        infoWindowRef.current.open(map, midpointMarker);
      });

      markersRef.current.push(midpointMarker);
    }

    // Place markers
    (results.restaurants || []).forEach((place) => {
      const loc = place.geometry?.location;
      const lat = loc ? (typeof loc.lat === 'function' ? loc.lat() : loc.lat) : null;
      const lng = loc ? (typeof loc.lng === 'function' ? loc.lng() : loc.lng) : null;
      if (lat == null || lng == null) return;

      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        map,
        title: place.name,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
              <circle cx="14" cy="14" r="12" fill="${searchType === 'hotel' ? '#ef4444' : '#10b981'}" stroke="white" stroke-width="2"/>
              <circle cx="14" cy="14" r="6" fill="white"/>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(28, 28),
        },
      });

      marker.addListener('click', () => {
        const rating = place.rating ? `⭐ ${place.rating}` : 'Pas de note';
        const price = place.price_level ? '€'.repeat(place.price_level) : 'Prix non spécifié';
        const distance = Math.round(place.distance_from_midpoint ?? 0);
        const openStatus = place.opening_hours?.open_now !== undefined
          ? (place.opening_hours.open_now ? '<span style="color: green">🟢 Ouvert</span>' : '<span style="color: red">🔴 Fermé</span>')
          : '';

        const contactButtons = [];
        if (place.phone_number) {
          contactButtons.push(`<a href="tel:${place.phone_number}" style="background: #10b981; color: white; padding: 4px 8px; border-radius: 4px; text-decoration: none; font-size: 12px;">📞 Appeler</a>`);
        }
        if (place.reservation_url) {
          contactButtons.push(`<a href="${place.reservation_url}" target="_blank" style="background: #3b82f6; color: white; padding: 4px 8px; border-radius: 4px; text-decoration: none; font-size: 12px;">${searchType === 'hotel' ? '🏨' : '🍽️'} Réserver</a>`);
        }
        if (place.google_maps_url) {
          contactButtons.push(`<a href="${place.google_maps_url}" target="_blank" style="background: #ef4444; color: white; padding: 4px 8px; border-radius: 4px; text-decoration: none; font-size: 12px;">📍 Ouvrir</a>`);
        }

        if (!infoWindowRef.current) return;
        infoWindowRef.current.setContent(`
          <div style="min-width: 250px; max-width: 300px;">
            ${place.photo_url ? `<img src="${place.photo_url}" alt="${place.name}" style="width: 100%; height: 120px; object-fit: cover; border-radius: 8px; margin-bottom: 8px;">` : ''}
            <h3 style="font-weight: bold; margin: 0 0 8px 0; color: #1f2937;">${place.name}</h3>
            <div style="font-size: 14px; color: #6b7280; margin-bottom: 8px;">
              <div style="margin-bottom: 4px;">${place.address || ''}</div>
              <div style="margin-bottom: 4px;">${rating} • ${price}</div>
              ${Number.isFinite(distance) && distance > 0 ? `<div style="margin-bottom: 4px;">Distance: ${distance}m du point médian</div>` : ''}
              ${openStatus ? `<div style="margin-bottom: 8px;">${openStatus}</div>` : ''}
            </div>
            <div style="display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 8px;">
              ${contactButtons.join(' ')}
            </div>
            <button 
              onclick="window.__handlePlaceSelect && window.__handlePlaceSelect('${place.place_id}')" 
              style="background: #6366f1; color: white; padding: 6px 12px; border: none; border-radius: 6px; cursor: pointer; font-size: 12px; width: 100%;"
            >
              ℹ️ Plus d'informations
            </button>
          </div>
        `);
        infoWindowRef.current.open(map, marker);
      });

      markersRef.current.push(marker);
    });

    // Marker clusterer
    const renderer = {
      render: ({ count, position }) => {
        const size = count > 50 ? 56 : count > 20 ? 46 : 36;
        const color = count > 50 ? '#7c3aed' : count > 20 ? '#2563eb' : '#10b981';
        const svg = `
          <svg width="${size}" height="${size}" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="20" r="20" fill="${color}" opacity="0.85" />
            <circle cx="20" cy="20" r="14" fill="white" opacity="0.9"/>
            <text x="20" y="23" text-anchor="middle" font-family="Arial" font-weight="bold" font-size="14" fill="${color}">${count}</text>
          </svg>`;
        return new window.google.maps.Marker({
          position,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
            scaledSize: new window.google.maps.Size(size, size),
          },
          zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
        });
      },
    };

    clustererRef.current = new MarkerClusterer({ map, markers: markersRef.current, renderer });

    // Global handler for InfoWindow buttons
    window.__handlePlaceSelect = (placeId) => {
      if (onPlaceSelect) onPlaceSelect(placeId);
      if (infoWindowRef.current) infoWindowRef.current.close();
    };

    // Cleanup on unmount / change
    return () => {
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current = null;
      }
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
    };
  }, [map, results, searchType, onPlaceSelect]);

  return (
    <div
      ref={initializeMap}
      className="w-full h-96 rounded-lg border border-gray-300"
      style={{ minHeight: '400px' }}
    />
  );
}

export default function ResultsMapView({ results, onPlaceSelect, searchType }) {
  if (!results || !results.restaurants || results.restaurants.length === 0) {
    return (
      <div className="w-full h-96 rounded-lg border border-gray-300 flex items-center justify-center bg-gray-50">
        <div className="text-center text-gray-500">
          <p className="text-lg mb-2">🗺️</p>
          <p>Aucun résultat à afficher sur la carte</p>
          <p className="text-sm">Effectuez une recherche pour voir les résultats</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          📍 Carte des résultats ({results.total_count} {searchType === 'hotel' ? 'hôtels' : 'restaurants'})
        </h3>
        <div className="flex items-center space-x-4 text-sm text-gray-600">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-blue-500 rounded-full mr-2"></div>
            Point de rencontre
          </div>
          <div className="flex items-center">
            <div className={`w-4 h-4 ${searchType === 'hotel' ? 'bg-red-500' : 'bg-green-500'} rounded-full mr-2`}></div>
            {searchType === 'hotel' ? 'Hôtels' : 'Restaurants'}
          </div>
        </div>
      </div>

      {/* If you already load Google Maps via script in index.html you can remove Wrapper here
          and render <ResultsMap ... /> directly. */}
      <Wrapper apiKey={GOOGLE_MAPS_API_KEY} libraries={['places']}>
        <ResultsMap results={results} onPlaceSelect={onPlaceSelect} searchType={searchType} />
      </Wrapper>
    </div>
  );
}
